/**
 * Update date: 22-05-2023
 * Screen 2017.1
 */
import PopupSelectCommonCode from 'app/components/PopupCommon/PopupSelectCommonCode';
import Form from 'devextreme-react/form';
import React, { useState, useRef, useEffect } from 'react';
import { uniqBy, isEqual } from 'lodash';
import DateOptions from 'app/components/DateOptions';
import PopupSelectProduct from 'app/components/PopupCommon/PopupSelectProduct';
import PopupSelectProductGroup from 'app/components/PopupCommon/PopupSelectProductGroup';
import Collapse from 'app/components/Collapse';
import useFormatTime from 'hooks/useFormatTime';
import moment from 'moment';
import { useMallProductMaster } from '..';
import './style.scss';
import useDictionary from 'hooks/useDictionary';

const listTypeCommon = {
  largeCategory: {
    codeType: 'LC',
    title: 'Search Large Category',
  },
  mediumCategory: {
    codeType: 'MC',
    title: 'Search Medium Category',
  },
  smallCategory: {
    codeType: 'SC',
    title: 'Search Small Category',
  },
  subCategory: {
    codeType: 'UC',
    title: 'Search Sub Category',
  },
  productType: {
    codeType: 'PT',
  },
};

const FormSearch = () => {
  const mallProductMasterContext: any = useMallProductMaster();
  const { params, setParams, refetchData, productTpPermission } =
    mallProductMasterContext || {};
  const [formStore, setFormStore] = useState<any>({
    largeCategory: [],
    mediumCategory: [],
    smallCategory: [],
    subCategory: [],
    product: [],
    productGroup: [],
    mallInfo: [],
    productType: [],
  });
  const [formData, setFormData] = useState<any>({
    fromDate: params?.fromDate,
    toDate: params?.toDate,
    largeCategory: '',
    mediumCategory: '',
    smallCategory: '',
    subCategory: '',
    product: '',
    productGroup: '',
    mallInfo: '',
    productType: '',
  });
  const [showPopupByType, setShowPopupByType] = useState('');
  const formRef: any = useRef(null);
  const collapseRef: any = useRef(null);
  const { DateFormat } = useFormatTime();

  const { t }: any = useDictionary({
    programId: '2017.1',
  });
  /**
   * fetch data
   */
  useEffect(() => {
    if (!productTpPermission?.length) return;
    setFormStore(preState => ({
      ...preState,
      productType: productTpPermission,
    }));
    setFormData(preState => ({
      ...preState,
      isReadOnlyProductTp: productTpPermission?.length > 0,
    }));
  }, [productTpPermission]);

  const onSubmit = () => {
    collapseRef?.current?.onCollappse();
    const isValid = formRef?.current?.instance?.validate()?.isValid;
    if (isValid) {
      const searchParams = {
        largeCategory: formStore.largeCategory.map((o: any) => o?.code),
        mediumCategory: formStore.mediumCategory.map((o: any) => o?.code),
        smallCategory: formStore.smallCategory.map((o: any) => o?.code),
        subCategory: formStore.subCategory.map((o: any) => o?.code),
        productIds: formStore.product.map((o: any) => o?.productId),
        productGroupIds: formStore.productGroup.map(
          (o: any) => o?.productGroupId,
        ),
        productTypes: formStore.productType.map((o: any) => o?.code),

        // mallInfoId: formStore.mallInfo.map((o: any) => o?.mallInfoId)?.[0],
        mallCode: 'AOS',
        fromDate: formData?.fromDate
          ? moment(formData?.fromDate).format('YYYY-MM-DD')
          : null,
        toDate: formData?.toDate
          ? moment(formData?.toDate).format('YYYY-MM-DD')
          : null,
      };
      if (!isEqual(params, searchParams)) {
        setParams && setParams(searchParams);
      }
      refetchData({
        data: {
          ...searchParams,
        },
      });
    } else {
      collapseRef?.current?.onCollappse();
    }
  };

  /**
   * on remove store
   *
   * @param {*} storeType
   * @param {*} index
   */
  const removeStore = (storeType, index) => {
    let newStore;
    if (index === -1) {
      setFormStore({ ...formStore, [storeType]: [] });
    } else {
      const l = [...formStore[storeType]];
      newStore = [...l.slice(0, index), ...l.slice(index + 1)];
      setFormStore({ ...formStore, [storeType]: newStore });
    }
  };

  /**
   * on update store
   *
   * @param {*} storeType
   * @param {*} data
   * @param {*} [key=false]
   */
  const updateStore = (storeType, data, key: any = false) => {
    let filterData: any = [data];
    if (key) {
      filterData = uniqBy([...formStore?.[storeType], ...data], key);
    }
    const newStore = { ...formStore, [storeType]: filterData };
    setFormData({ ...formData, [storeType]: '' });
    setFormStore({ ...newStore });
    setShowPopupByType('');
  };

  /**
   * on generate textbox
   *
   * @param {string} fieldLabel
   * @param {string} fieldName
   * @param {string} fieldBtnText
   * @param {number} [numberShowMore=2]
   * @param {boolean} [readOnly=false]
   * @return {*}  {*}
   */
  const generateDxTextBox = (
    fieldLabel: string,
    fieldName: string,
    fieldBtnText: string,
    numberShowMore = 2,
    readOnly = false,
  ): any => {
    const dataList = formStore?.[fieldName] || [];
    const buttons = dataList.slice(0, numberShowMore).map((data, index) => {
      return {
        name: `${fieldName}${index}`,
        location: 'before',
        options: {
          icon: 'close',
          text: data?.[fieldBtnText],
          onClick: () => removeStore(fieldName, index),
        },
      };
    });
    if (dataList.length > numberShowMore) {
      buttons.push({
        name: `${fieldName}More`,
        location: 'before',
        options: {
          text: `+ ${dataList.length - numberShowMore} ${t('more')}}`,
        },
      });
    }
    if (dataList.length > 0) {
      buttons.push({
        name: 'clearAll',
        location: 'after',
        options: {
          stylingMode: 'text',
          icon: 'close',
          onClick: () => removeStore(fieldName, -1),
        },
      });
    }
    buttons.push({
      name: 'search',
      location: 'after',
      options: {
        stylingMode: 'text',
        icon: 'search',
        onClick: () => setShowPopupByType(fieldName),
      },
    });

    return {
      label: { text: t(fieldLabel) },
      dataField: fieldName,
      editorType: 'dxTextBox',
      editorOptions: {
        readOnly: readOnly || false,
        onEnterKey: () => setShowPopupByType(fieldName),
        showClearButton: true,
        buttons: buttons,
      },
    };
  };

  /**
   * render popup by type
   *
   * @param {string} popupType
   * @return {*}
   */
  const getPopupByType = (popupType: string) => {
    switch (popupType) {
      // case 'mall':
      //   return (
      //     <PopupSelectMall
      //       visible={popupType === 'mall'}
      //       text={formData?.mallInfo}
      //       mode="single"
      //       onHiding={() => setShowPopupByType('')}
      //       onSubmit={data => updateStore('mallInfo', data, false)}
      //     />
      //   );
      case 'productGroup':
        return (
          <PopupSelectProductGroup
            visible={popupType === 'productGroup'}
            text={formData?.productGroup}
            mode="multiple"
            onHiding={() => setShowPopupByType('')}
            onSubmit={data =>
              updateStore('productGroup', data, 'productGroupId')
            }
          />
        );
      case 'product':
        return (
          <PopupSelectProduct
            visible={popupType === 'product'}
            text={formData?.product}
            mode="multiple"
            onHiding={() => setShowPopupByType('')}
            onSubmit={data => updateStore('product', data, 'productId')}
          />
        );
      case 'largeCategory':
      case 'mediumCategory':
      case 'smallCategory':
      case 'subCategory':
      case 'productType':
        return (
          <PopupSelectCommonCode
            visible={!!listTypeCommon?.[popupType]?.codeType}
            text={formData?.[popupType]}
            codeType={listTypeCommon?.[popupType]?.codeType}
            mode="multiple"
            onHiding={() => setShowPopupByType('')}
            onSubmit={data => updateStore(popupType, data, 'code')}
          />
        );
      default:
        return null;
    }
  };

  /**
   * on field data change
   *
   * @param {*} e
   */
  const onFieldDataChanged = e => {
    switch (e?.dataField) {
      case 'fromDate':
        if (formData.toDate && e?.value && e?.value > formData.toDate) {
          formRef?.current?.instance.updateData('toDate', null);
        }
        break;
      case 'toDate':
        if (formData.fromDate && e?.value && e?.value < formData.fromDate)
          formRef?.current?.instance.updateData('fromDate', null);
        break;
      default:
        break;
    }
  };

  // Onchange date when choose date option
  const onChangeDate = (fromDateValue, toDateValue) => {
    formRef?.current?.instance?.updateData('fromDate', fromDateValue);
    formRef?.current?.instance?.updateData('toDate', toDateValue);
  };

  /**
   * render search
   *
   * @param {*} { name }
   * @return {*}
   */
  const Search = ({ name }) => {
    return (
      <>
        <DateOptions
          onChangeValue={(fromDate, toDate) => onChangeDate(fromDate, toDate)}
          optionSelectAll={true}
        />
        <Form
          ref={formRef}
          formData={formData}
          showColonAfterLabel={false}
          items={[
            {
              label: { text: t('Product Change Date') },
              dataField: 'fromDate',
              editorType: 'dxDateBox',
              editorOptions: {
                displayFormat: DateFormat,
              },
            },
            {
              label: { text: ' ' },
              dataField: 'toDate',
              editorType: 'dxDateBox',
              cssClass: 'to-date-customize',
              editorOptions: {
                displayFormat: DateFormat,
              },
            },
            // {
            //   label: { text: t('Mall') },
            //   dataField: 'mallInfo',
            //   editorType: 'dxTextBox',
            //   colSpan: 2,
            //   isRequired: !formStore?.mallInfo?.[0]?.mallInfoId ? true : false,
            //   editorOptions: {
            //     onEnterKey: () => setShowPopupByType('mall'),
            //     buttons: [
            //       {
            //         name: 'data1',
            //         location: 'before',
            //         options: {
            //           icon: 'close',
            //           text: formStore?.mallInfo?.[0]?.mallName,
            //           onClick: () => removeStore('mallInfo', 0),
            //           visible: !!formStore?.mallInfo?.[0]?.mallInfoId,
            //           rtlEnabled: true,
            //         },
            //       },

            //       {
            //         name: 'clearAll',
            //         location: 'after',
            //         options: {
            //           stylingMode: 'text',
            //           icon: 'close',
            //           visible: !!formStore?.mallInfo?.[0]?.mallInfoId,
            //           onClick: () => removeStore('mallInfo', -1),
            //         },
            //       },
            //       {
            //         name: 'search',
            //         location: 'after',
            //         options: {
            //           stylingMode: 'text',
            //           icon: 'search',
            //           onClick: () => setShowPopupByType('mall'),
            //         },
            //       },
            //     ],
            //   },
            // },
          ]}
          labelLocation="top"
          colCount={4}
          onFieldDataChanged={onFieldDataChanged}
        />
      </>
    );
  };

  /**
   * render advanced search
   *
   * @param {*} { name }
   * @return {*}
   */
  const AdvancedSearch = ({ name }) => {
    return (
      <>
        <Form
          formData={formData}
          showColonAfterLabel={false}
          items={[
            generateDxTextBox('Large Category', 'largeCategory', 'name'),
            generateDxTextBox('Medium Category', 'mediumCategory', 'name'),
            generateDxTextBox(
              'Product Group',
              'productGroup',
              'productGroupNm',
            ),
            generateDxTextBox('Small Category', 'smallCategory', 'name'),
            generateDxTextBox('Sub Category', 'subCategory', 'name'),
            generateDxTextBox('Product', 'product', 'productNm'),
            generateDxTextBox(
              'Product Type',
              'productType',
              'name',
              2,
              formData?.isReadOnlyProductTp,
            ),
          ]}
          labelLocation="top"
          colCount={3}
          onFieldDataChanged={onFieldDataChanged}
        />
      </>
    );
  };

  return (
    <div className="mall-product-master-search-form">
      <Collapse onSubmit={onSubmit} ref={collapseRef}>
        <Search name={t('Search')} />
        <AdvancedSearch name={t('Advanced Search')} />
      </Collapse>
      {getPopupByType(showPopupByType)}
    </div>
  );
};

export default FormSearch;
